import axios from 'axios';
import { Message } from 'ant-design-vue';
import store from "../store/index";
import router from '@/router/index'; // 导入路由
import { ref } from 'vue';

// 登录状态标识
export const isLoginIn = ref(true);
// 是否重复弹出登录过期提示（防止多个401接口并发重复弹窗）
export const isRelogin = { show: false };

// 创建axios实例
const request = axios.create({
  baseURL: process.env.VUE_APP_HOST,
  timeout: 50000, // 50秒超时
});

/**
 * 退出登录 清除登录信息 + 跳转首页/登录页
 */
export function onLogout() {
  // 清除vuex登录状态
  store.commit("LOGOUT");
  // 清除本地token
  localStorage.removeItem('token');
  // 修改登录标识
  isLoginIn.value = false;
  // 跳转登录页
  router.push({ path: "/index" });
  Message.success('登录已过期，请重新登录');
}

/**
 * 关闭全局loading自旋
 */
function closeSpinning() {
  store.commit("CHANGE_GLOBALSPINNING", false);
}

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 无 noSpinning 参数则打开loading
    if (!config.params?.noSpinning) {
      store.commit("CHANGE_GLOBALSPINNING", true);
    }

    // 携带token请求头
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    closeSpinning();
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    closeSpinning();
    const { data, config } = response;

    // 1. 文件流blob 直接返回原始数据
    if (config.responseType === 'blob') {
      return data;
    }

    // 2. 白名单接口，直接放行原始返回
    const whiteListUrl = [
      'media/wechat/member/queryIsMember',
      'media/wechat/userGoodsOrder/queryOrderStatus'
    ];
    if (whiteListUrl.some(url => config.url.includes(url))) {
      return data;
    }

    // 3. 业务状态码判断
    if (data.state !== undefined) {
      // 业务异常
      if (data.state !== 0) {
        Message.error(data.message || '接口请求失败');
        return Promise.reject(data);
      }
      // 业务正常返回data
      return data;
    }

    // 无业务state字段直接返回完整响应
    return data;
  },
  (error) => {
    closeSpinning();
    console.error('请求异常：', error);
    const response = error.response;

    // ===================== 核心：处理401登录过期 =====================
    if (response?.status === 401) {
      // 防止多个接口同时401重复弹窗
      if (!isRelogin.show) {
        isRelogin.show = true;
        Message.error('未登录或登录已过期，请重新登录');
        // 执行登出清除信息、跳转页面
        onLogout();
        // 延时解锁弹窗标识
        setTimeout(() => {
          isRelogin.show = false;
        }, 1500);
      }
      return Promise.reject(error);
    }

    // 后端返回自定义401业务码 state=-1（和你接口返回格式匹配）
    const resData = response?.data || {};
    if (resData.state === -1 && resData.message === '未登录或登录已过期') {
      if (!isRelogin.show) {
        isRelogin.show = true;
        Message.error(resData.message);
        onLogout();
        setTimeout(() => {
          isRelogin.show = false;
        }, 1500);
      }
      return Promise.reject(error);
    }

    // ===================== 其他网络/服务异常统一处理 =====================
    let errMsg = '';
    const errInfo = error.message || '';

    if (errInfo === "Network Error") {
      errMsg = "后端接口连接异常，请检查服务";
    } else if (errInfo.includes("timeout")) {
      errMsg = "接口请求超时，请稍后重试";
    } else if (errInfo.includes("Request failed with status code")) {
      const code = errInfo.slice(-3);
      if (code === '503') {
        errMsg = '服务器维护中，网络开小差了，请稍后再试';
      } else {
        errMsg = `系统接口${code}异常`;
      }
    } else {
      errMsg = errInfo;
    }

    Message.error(errMsg);
    return Promise.reject(error);
  }
);

// 示例接口导出
export const fetchData = () => request.get('/login');

export default request;